
import { deleteMindMap, MindMap, updateMindMap, getMindMapById } from '@/services/supabase/mind-maps';
import { supabase } from '@/services/supabase/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Define cache times
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = DAY_IN_MS * 30; // 30 days stale time
const GC_TIME = DAY_IN_MS * 60; // 60 days garbage collection

export const mindMapKeys = {
    all: ['content', 'mindmaps'] as const,
    list: (storyId: string) => [...mindMapKeys.all, 'list', storyId] as const,
    detail: (mindMapId: string) => [...mindMapKeys.all, 'detail', mindMapId] as const,
};

/**
 * Hook to fetch mind maps for a story
 */
export function useMindMaps(
    storyId: string | undefined | null,
    options: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: mindMapKeys.list(storyId || ''),
        queryFn: async () => {
            if (!storyId) return [];

            console.log(`Fetching mind maps for story ${storyId}`);

            const { data, error } = await supabase
                .from('mind_maps')
                .select('id, title, created_at, story_id')
                .eq('story_id', storyId)
                .order('title');

            if (error) {
                console.error('Error fetching mind maps:', error);
                throw error;
            }

            console.log(`Fetched ${data?.length || 0} mind maps for story ${storyId}`);
            return (data || []) as Partial<MindMap>[];
        },
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        enabled: !!storyId && (options.enabled !== false),
    });
}

/**
 * Hook to fetch a single mind map by ID
 */
export function useMindMap(
    mindMapId: string | undefined | null,
    options: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: mindMapKeys.detail(mindMapId || ''),
        queryFn: async () => {
            if (!mindMapId) return null;
            console.log(`Fetching mind map ${mindMapId}`);
            return await getMindMapById(mindMapId);
        },
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        enabled: !!mindMapId && (options.enabled !== false),
    });
}

/**
 * Hook to update a mind map's title or content
 */
export function useUpdateMindMap() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            mindMapId,
            title,
            description,
            content
        }: {
            mindMapId: string;
            title?: string;
            description?: string;
            content?: any;
        }) => {
            console.log(`Updating mind map ${mindMapId}`);
            return await updateMindMap(mindMapId, { title, description, content });
        },
        onSuccess: (updatedMindMap) => {
            // Update the mind map in the cache
            queryClient.setQueryData(
                mindMapKeys.detail(updatedMindMap.id),
                updatedMindMap
            );

            // Invalidate the mind maps list for the story
            if (updatedMindMap.story_id) {
                queryClient.invalidateQueries({
                    queryKey: mindMapKeys.list(updatedMindMap.story_id.toString())
                });
            }
        },
    });
}

/**
 * Hook to delete a mind map
 */
export function useDeleteMindMap() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            mindMapId,
            storyId
        }: {
            mindMapId: string;
            storyId: string
        }) => {
            console.log(`Deleting mind map ${mindMapId}`);
            await deleteMindMap(mindMapId);
            return { mindMapId, storyId };
        },
        onSuccess: (result) => {
            // Invalidate the mind maps list for the story
            queryClient.invalidateQueries({
                queryKey: mindMapKeys.list(result.storyId)
            });

            // Remove the mind map from the cache
            queryClient.removeQueries({
                queryKey: mindMapKeys.detail(result.mindMapId)
            });
        },
    });
}
